import React, { useState, useEffect, useCallback } from 'react';
import { movimientosProgramadosAPI, movimientosPredefinidosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';

const MESES = [
  { n: 1, label: 'Enero' }, { n: 2, label: 'Febrero' }, { n: 3, label: 'Marzo' },
  { n: 4, label: 'Abril' }, { n: 5, label: 'Mayo' }, { n: 6, label: 'Junio' },
  { n: 7, label: 'Julio' }, { n: 8, label: 'Agosto' }, { n: 9, label: 'Septiembre' },
  { n: 10, label: 'Octubre' }, { n: 11, label: 'Noviembre' }, { n: 12, label: 'Diciembre' },
];

const ENTIDAD_TIPOS = [
  { id: 'cliente', label: 'Clientes (todos o uno)' },
  { id: 'proveedor', label: 'Proveedores (todos o uno)' },
  { id: 'empleado', label: 'Empleados (todos o uno)' },
];

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MovimientosProgramados = () => {
  const { hasPermission } = useAuth();
  const { fechaHoy } = useZonaHoraria();
  const [listado, setListado] = useState([]);
  const [predefinidos, setPredefinidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [aplicandoId, setAplicandoId] = useState(null);
  const [form, setForm] = useState({
    movimiento_predefinido_id: '',
    entidad_tipo: 'cliente',
    entidad_id: '',
    monto: '',
    mes_aplicacion: String(new Date().getMonth() + 1),
    anio_aplicacion: String(new Date().getFullYear()),
    concepto: '',
  });

  const loadListado = useCallback(async () => {
    try {
      setLoading(true);
      const res = await movimientosProgramadosAPI.getAll();
      if (res.success) setListado(res.data || []);
    } catch (e) {
      toast.error(e.message || 'Error al cargar programaciones');
      setListado([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPredefinidos = useCallback(async () => {
    try {
      const res = await movimientosPredefinidosAPI.getAll({ activo: true });
      if (res.success) setPredefinidos(res.data || []);
    } catch {
      setPredefinidos([]);
    }
  }, []);

  useEffect(() => {
    loadListado();
    loadPredefinidos();
  }, [loadListado, loadPredefinidos]);

  useEffect(() => {
    const hoy = fechaHoy();
    if (hoy) {
      const [anio, mes] = hoy.split('-');
      setForm((prev) => ({
        ...prev,
        mes_aplicacion: prev.mes_aplicacion || String(parseInt(mes, 10)),
        anio_aplicacion: prev.anio_aplicacion || anio,
      }));
    }
  }, [fechaHoy]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!hasPermission('crear_movimientos_programados')) {
      toast.error('No tiene permiso para crear programaciones');
      return;
    }
    if (!form.movimiento_predefinido_id || !form.concepto.trim()) {
      return toast.error('Complete predefinido y concepto');
    }
    try {
      const res = await movimientosProgramadosAPI.create({
        movimiento_predefinido_id: Number(form.movimiento_predefinido_id),
        entidad_tipo: form.entidad_tipo,
        entidad_id: form.entidad_id ? Number(form.entidad_id) : null,
        monto: form.monto !== '' ? parseFloat(form.monto) : null,
        mes_aplicacion: parseInt(form.mes_aplicacion, 10),
        anio_aplicacion: parseInt(form.anio_aplicacion, 10),
        concepto: form.concepto.trim(),
      });
      if (res.success) {
        toast.success('Programación creada');
        setForm((prev) => ({ ...prev, concepto: '', monto: '', entidad_id: '' }));
        loadListado();
      } else {
        toast.error(res.message || 'No se pudo crear');
      }
    } catch (err) {
      toast.error(err.message || 'Error al crear');
    }
  };

  const handlePreview = async (id) => {
    if (!hasPermission('aplicar_movimientos_programados')) {
      toast.error('No tiene permiso para previsualizar');
      return;
    }
    setPreviewId(id);
    setPreview(null);
    try {
      const res = await movimientosProgramadosAPI.preview(id);
      if (res.success) setPreview(res.data);
      else toast.error(res.message || 'Error en preview');
    } catch (e) {
      toast.error(e.message || 'Error en preview');
    } finally {
      setPreviewId(null);
    }
  };

  const handleAplicar = async (id) => {
    if (!hasPermission('aplicar_movimientos_programados')) {
      toast.error('No tiene permiso para aplicar');
      return;
    }
    if (!window.confirm('¿Aplicar esta programación? Se crearán los movimientos pendientes.')) return;
    setAplicandoId(id);
    try {
      const res = await movimientosProgramadosAPI.aplicar(id);
      if (res.success) {
        toast.success(res.message || 'Movimientos aplicados');
        setPreview(null);
        loadListado();
      } else {
        toast.error(res.message || 'No se pudo aplicar');
      }
    } catch (e) {
      toast.error(e.message || 'Error al aplicar');
    } finally {
      setAplicandoId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!hasPermission('crear_movimientos_programados')) {
      toast.error('No tiene permiso');
      return;
    }
    if (!window.confirm('¿Desactivar esta programación?')) return;
    try {
      const res = await movimientosProgramadosAPI.delete(id);
      if (res.success) {
        toast.success('Programación desactivada');
        loadListado();
      } else {
        toast.error(res.message || 'No se pudo desactivar');
      }
    } catch (e) {
      toast.error(e.message || 'Error al desactivar');
    }
  };

  const mesLabel = (mes, anio) => {
    const m = MESES.find((x) => x.n === parseInt(mes, 10));
    return m ? `${m.label} ${anio}` : `${mes}/${anio}`;
  };

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Movimientos programados</h1>
      </div>

      <div className="pagina-listado-fijo">
      {hasPermission('crear_movimientos_programados') && (
        <form onSubmit={handleCreate} className="cliente-form" style={{ maxWidth: '900px', marginBottom: 24 }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Nueva programación</h2>
          <div className="form-row">
            <div className="form-group">
              <label>Movimiento predefinido *</label>
              <select
                value={form.movimiento_predefinido_id}
                onChange={(e) => setForm((p) => ({ ...p, movimiento_predefinido_id: e.target.value }))}
                required
              >
                <option value="">Seleccione...</option>
                {predefinidos.map((p) => (
                  <option key={p.id} value={p.id}>{p.descripcion} ({p.tipo_movimiento})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Entidad *</label>
              <select
                value={form.entidad_tipo}
                onChange={(e) => setForm((p) => ({ ...p, entidad_tipo: e.target.value, entidad_id: '' }))}
              >
                {ENTIDAD_TIPOS.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>ID entidad (vacío = todas)</label>
              <input
                type="number"
                min="1"
                value={form.entidad_id}
                onChange={(e) => setForm((p) => ({ ...p, entidad_id: e.target.value }))}
                placeholder="Opcional"
              />
            </div>
            <div className="form-group">
              <label>Monto fijo (opcional)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.monto}
                onChange={(e) => setForm((p) => ({ ...p, monto: e.target.value }))}
                placeholder="Abono/sueldo si vacío"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Mes aplicación *</label>
              <select
                value={form.mes_aplicacion}
                onChange={(e) => setForm((p) => ({ ...p, mes_aplicacion: e.target.value }))}
              >
                {MESES.map((m) => (
                  <option key={m.n} value={m.n}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Año aplicación *</label>
              <input
                type="number"
                min="2000"
                max="2100"
                value={form.anio_aplicacion}
                onChange={(e) => setForm((p) => ({ ...p, anio_aplicacion: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label>Concepto *</label>
            <input
              type="text"
              value={form.concepto}
              onChange={(e) => setForm((p) => ({ ...p, concepto: e.target.value }))}
              required
              placeholder="Concepto del movimiento programado"
            />
          </div>
          <button type="submit" className="btn-primary">Crear programación</button>
        </form>
      )}

      {preview && (
        <div style={{ marginBottom: 24, padding: 16, background: '#f0f4f8', borderRadius: 8, border: '1px solid #ddd' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <strong>Vista previa — total pendiente: ${formatMoneda(preview.total)}</strong>
            <button type="button" className="btn-secondary" onClick={() => setPreview(null)}>Cerrar</button>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Entidad</th>
                  <th>Nombre</th>
                  <th>Monto</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {(preview.items || []).map((item) => (
                  <tr key={`${item.entidad_tipo}-${item.entidad_id}`}>
                    <td>{item.entidad_tipo}</td>
                    <td>{item.nombre}</td>
                    <td>${formatMoneda(item.monto)}</td>
                    <td>{item.ya_aplicado ? 'Ya aplicado' : 'Pendiente'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className="listado-tabla-area clientes-listado-tabla-area">
            <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Período</th>
              <th>Predefinido</th>
              <th>Entidad</th>
              <th>Concepto</th>
              <th>Monto</th>
              <th>Usuario</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="no-data">Cargando...</td></tr>
            ) : listado.length === 0 ? (
              <tr><td colSpan="7" className="no-data">No hay programaciones</td></tr>
            ) : (
              listado.filter((p) => p.activo !== 0).map((p) => (
                <tr key={p.id}>
                  <td>{mesLabel(p.mes_aplicacion, p.anio_aplicacion)}</td>
                  <td>{p.predefinido_descripcion || p.movimiento_predefinido_id}</td>
                  <td>{p.entidad_tipo}{p.entidad_id ? ` #${p.entidad_id}` : ' (todos)'}</td>
                  <td>{p.concepto}</td>
                  <td>{p.monto != null ? `$${formatMoneda(p.monto)}` : '(automático)'}</td>
                  <td>{p.usuario_nombre || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      {hasPermission('aplicar_movimientos_programados') && (
                        <>
                          <button
                            type="button"
                            className="btn-edit"
                            onClick={() => handlePreview(p.id)}
                            disabled={previewId === p.id}
                          >
                            {previewId === p.id ? '…' : 'Preview'}
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => handleAplicar(p.id)}
                            disabled={aplicandoId === p.id}
                          >
                            {aplicandoId === p.id ? 'Aplicando…' : 'Aplicar'}
                          </button>
                        </>
                      )}
                      {hasPermission('crear_movimientos_programados') && (
                        <button type="button" className="btn-delete" onClick={() => handleDelete(p.id)}>
                          Desactivar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovimientosProgramados;
